import { useState, useEffect } from 'react';
import moment from 'moment';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import { ReactComponent as ViewIcon } from '@Assets/view4.svg';
import { ReactComponent as AccessIcon } from '@Assets/access.svg';
import { ReactComponent as DefaultFormPic } from '@Assets/form.svg';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import KaiziqResolveConnectionAnalyticsService from '@Helpers/URLHelpers/KaiziqResolve/views/KaiziqResolveConnectionAnalyticsService';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import './FormCard.scss';

export default function FormCard(props) {
    const [kaiziqResolveConnection, setKaiziqResolveConnection] = useState({});
    const [kaiziqResolveConnectionAnalytics, setKaiziqResolveConnectionAnalytics] = useState({});
    const [details, setDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const kaiziqResolveConnectionAnalyticsService = new KaiziqResolveConnectionAnalyticsService();
    const className = 'formCard';

    useEffect(() => {
        parseData();
    }, [props]);

    const parseData = async () => {
        try {
            setLoading(true);
            setKaiziqResolveConnection(props.kaiziqResolveConnection);
            let detailsObj = JSON.parse(props.kaiziqResolveConnection.details);
            setDetails(detailsObj);
            let kaiziqResolveConnectionAnalyticsObj = await kaiziqResolveConnectionAnalyticsService.getByFormId(
                props.kaiziqResolveConnection?.id
            );
            kaiziqResolveConnectionAnalyticsObj = JSON.parse(kaiziqResolveConnectionAnalyticsObj.body);
            setKaiziqResolveConnectionAnalytics(kaiziqResolveConnectionAnalyticsObj);
        } catch (err) {
            console.error('Error parsing form card data:', err);
        } finally {
            setLoading(false);
        }
    };

    const Capitalize = (str) => {
        return str?.charAt(0)?.toUpperCase() + str?.slice(1);
    };

    const getFormTypeLabel = () => {
        const formType = details?.settings?.formType;
        switch (formType) {
            case 'ticket':
                return 'Ticketing Form';
            case 'issue':
                return 'Issue Reporting';
            case 'feedback':
                return 'Feedback Form';
            case 'contact':
                return 'Contact Form';
            case 'it_service':
                return 'IT Service Desk';
            case 'simple':
                return 'Simple Form';
            default:
                return 'General Form';
        }
    };

    const getAccessTypeLabel = () => {
        const accessType = kaiziqResolveConnection?.accessType;
        if (accessType === 'public_open') return 'Open';
        if (accessType === 'public_verified' || accessType === 'private_verified') return 'Account Verified';
        if (accessType === 'public_email' || accessType === 'private_email') return 'Email Verified';
        return 'Unknown';
    };

    const dataURLtoFile = (dataurl) => {
        try {
            const arr = dataurl.split(',');
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) u8arr[n] = bstr.charCodeAt(n);
            return new File([u8arr], 'file', { type: mime });
        } catch (err) {
            return null;
        }
    };

    if (loading) {
        return (
            <div className={className + ' ' + className + '--loading component-shadow'}>
                <div className={className + '__header'}>
                    <Skeleton variant="text" width="30%" height={24} />
                    <Skeleton variant="rectangular" width={80} height={24} style={{ borderRadius: '12px' }} />
                </div>
                <div className={className + '__body'}>
                    <Skeleton variant="text" width="90%" height={28} />
                    <Skeleton variant="text" width="60%" height={20} />
                </div>
                <div className={className + '__footer'}>
                    <Skeleton variant="text" width="45%" height={20} />
                    <Skeleton variant="text" width="45%" height={20} />
                </div>
                <div className={className + '__stats'}>
                    <Skeleton variant="rectangular" width="32%" height={60} style={{ borderRadius: '10px' }} />
                    <Skeleton variant="rectangular" width="32%" height={60} style={{ borderRadius: '10px' }} />
                    <Skeleton variant="rectangular" width="32%" height={60} style={{ borderRadius: '10px' }} />
                </div>
            </div>
        );
    }

    return (
        <div onClick={(e) => props.handleFormClick(e, props.index)} className={className + ' component-shadow click'}>
            {/* Status Badge */}
            <div className={className + '__statusBadge'}>
                <Chip
                    label={kaiziqResolveConnection?.connectionLive ? 'Live' : 'Development'}
                    size="small"
                    className={
                        kaiziqResolveConnection?.connectionLive
                            ? className + '__statusBadge--live'
                            : className + '__statusBadge--dev'
                    }
                />
            </div>

            {/* Header with Photo */}
            <div className={className + '__header'}>
                <div className={className + '__header__photoContainer'}>
                    {!!kaiziqResolveConnection?.photo ? (
                        <img
                            src={URL.createObjectURL(dataURLtoFile(kaiziqResolveConnection?.photo))}
                            alt="form"
                            className={className + '__header__photo'}
                        />
                    ) : (
                        <div className={className + '__header__photoPlaceholder'}>
                            <DefaultFormPic />
                        </div>
                    )}
                </div>
                <div className={className + '__header__info'}>
                    <div className={className + '__header__id'}>{kaiziqResolveConnection.formId}</div>
                    <div className={className + '__header__formType'}>{getFormTypeLabel()}</div>
                </div>
            </div>

            {/* Title */}
            <div className={className + '__body'}>
                <div className={className + '__body__title'}>{kaiziqResolveConnection.title}</div>
            </div>

            {/* Metadata */}
            <div className={className + '__meta'}>
                <div className={className + '__meta__item'}>
                    <ViewIcon className={className + '__meta__icon'} />
                    <span>{Capitalize(kaiziqResolveConnection?.visibility)}</span>
                </div>
                <div className={className + '__meta__item'}>
                    <AccessIcon className={className + '__meta__icon'} />
                    <span>{getAccessTypeLabel()}</span>
                </div>
            </div>

            {/* Stats Section */}
            <div className={className + '__stats'}>
                <div className={className + '__stats__item'}>
                    <div className={className + '__stats__item__icon'}>
                        <ConfirmationNumberOutlinedIcon />
                    </div>
                    <div className={className + '__stats__item__content'}>
                        <div className={className + '__stats__item__label'}>Tickets</div>
                        <div className={className + '__stats__item__value'}>
                            {kaiziqResolveConnectionAnalytics?.totalTickets || 0}
                        </div>
                    </div>
                </div>
                <div className={className + '__stats__item'}>
                    <div className={className + '__stats__item__icon stats-icon--success'}>
                        <TrendingUpIcon />
                    </div>
                    <div className={className + '__stats__item__content'}>
                        <div className={className + '__stats__item__label'}>Resolved</div>
                        <div className={className + '__stats__item__value'}>
                            {kaiziqResolveConnectionAnalytics?.resolvedTickets || 0}
                        </div>
                    </div>
                </div>
                <div className={className + '__stats__item'}>
                    <div className={className + '__stats__item__icon stats-icon--info'}>
                        <CalendarTodayOutlinedIcon />
                    </div>
                    <div className={className + '__stats__item__content'}>
                        <div className={className + '__stats__item__label'}>Created</div>
                        <div className={className + '__stats__item__value'}>
                            {moment(new Date(kaiziqResolveConnection?.createTimeStamp)).format('MMM DD')}
                        </div>
                    </div>
                </div>
            </div>

            {/* Hover Overlay Effect */}
            <div className={className + '__overlay'}>
                <div className={className + '__overlay__text'}>View Details</div>
            </div>
        </div>
    );
}
